import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowUpDown, ArrowUp, ArrowDown, Eye, Users } from "lucide-react";
import { SOURCE_LABELS, SOURCE_COLORS, getInitials, formatDateShort } from "./customersUtils";

function HealthDot({ lastOrderAt }) {
  if (!lastOrderAt) return null;
  const days = Math.floor((Date.now() - new Date(lastOrderAt)) / 86400000);
  const color = days < 30 ? "bg-emerald-400" : days < 60 ? "bg-yellow-400" : "bg-red-400";
  const title = days < 30 ? `Ativo (${days}d)` : days < 60 ? `Em risco (${days}d)` : `Inativo (${days}d)`;
  return (
    <span
      className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-white ${color}`}
      title={title}
    />
  );
}

function SortHeader({ field, sortBy, sortDir, onSort, children }) {
  const active = sortBy === field;
  return (
    <button className="flex items-center gap-1 hover:text-foreground transition-colors" onClick={() => onSort(field)}>
      {children}
      {active
        ? (sortDir === "desc" ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />)
        : <ArrowUpDown className="w-3 h-3 opacity-40" />}
    </button>
  );
}

export function CustomerTable({ customers, sortBy, sortDir, onSort, onSelect, formatPrice }) {
  return (
    <div className="rounded-lg border border-border/50 overflow-hidden bg-card">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/30 hover:bg-muted/30">
            <TableHead className="w-[280px] pl-4"><SortHeader field="name" sortBy={sortBy} sortDir={sortDir} onSort={onSort}>Cliente</SortHeader></TableHead>
            <TableHead className="hidden md:table-cell"><SortHeader field="total_orders" sortBy={sortBy} sortDir={sortDir} onSort={onSort}>Pedidos</SortHeader></TableHead>
            <TableHead><SortHeader field="total_spent" sortBy={sortBy} sortDir={sortDir} onSort={onSort}>Total Gastado</SortHeader></TableHead>
            <TableHead className="hidden lg:table-cell"><SortHeader field="last_order_at" sortBy={sortBy} sortDir={sortDir} onSort={onSort}>Último Pedido</SortHeader></TableHead>
            <TableHead className="hidden lg:table-cell">Canal</TableHead>
            <TableHead className="w-[80px] text-center">Detalle</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {customers.map(c => (
            <TableRow key={c.phone} className="cursor-pointer group" onClick={() => onSelect(c)} data-testid={`customer-row-${c.phone}`}>
              <TableCell className="pl-4">
                <div className="flex items-center gap-3">
                  <div className="relative shrink-0">
                    <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">{getInitials(c.name)}</div>
                    <HealthDot lastOrderAt={c.last_order_at} />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate">{c.name || "Sin nombre"}</p>
                    <p className="text-xs text-muted-foreground truncate">{c.phone}</p>
                  </div>
                </div>
              </TableCell>
              <TableCell className="hidden md:table-cell"><span className="font-semibold">{c.total_orders}</span></TableCell>
              <TableCell><span className="font-bold text-primary">{formatPrice(c.total_spent)}</span></TableCell>
              <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">{formatDateShort(c.last_order_at)}</TableCell>
              <TableCell className="hidden lg:table-cell">
                <Badge variant="secondary" className={`text-[10px] ${SOURCE_COLORS[c.source] || "bg-gray-100 text-gray-600"}`}>{SOURCE_LABELS[c.source] || c.source}</Badge>
              </TableCell>
              <TableCell className="text-center">
                <Button variant="ghost" size="icon" className="h-8 w-8 opacity-60 group-hover:opacity-100" data-testid={`view-customer-${c.phone}`}><Eye className="w-4 h-4" /></Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {customers.length === 0 && (
        <div className="py-16 text-center">
          <Users className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-sm text-muted-foreground">No se encontraron clientes</p>
        </div>
      )}
    </div>
  );
}
